import React from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Textarea from '../../../components/ui/Textarea';
import CollapsibleSection from '../../../components/form/CollapsibleSection';
import FormSegmentedControl from '../../../components/form/FormSegmentedControl';
import McpServerBindingsEditor from './McpServerBindingsEditor';
import {
  GUARDRAIL_ACTION_OPTIONS,
  KNOWLEDGE_MAX_EVIDENCES_OPTIONS,
  KNOWLEDGE_MAX_EVIDENCES_UNLIMITED,
  KNOWLEDGE_RESPONSE_MODE_OPTIONS,
  KNOWLEDGE_RETRIEVAL_MODE_OPTIONS,
  RELEVANCE_MODE_OPTIONS,
} from '../constants';

function SubAgentAdvancedOptions({
  subAgent,
  onChange,
  llmModelLines = [],
  llmEfforts = [],
  mcpServers = [],
  onRequestImproveMcpInstructions,
  improvingMcpBindingId = null,
  isMcpImproveDisabled = false,
  getMcpImproveError,
  subAgents = [],
}) {
  const inheritProfile = subAgent.inheritLlmProfile;
  const isDiverseRetrieval = (subAgent.knowledgeRetrievalMode || 'focused') === 'diverse';
  const otherSkills = subAgents.filter(
    (item) => item.id !== subAgent.id && item.type === 'skills'
  );

  const modelLineOptions = llmModelLines.map((line) => ({ value: line.id, label: line.label }));
  const effortOptions = llmEfforts.map((effort) => ({ value: effort.id, label: effort.label }));

  return (
    <CollapsibleSection
      title="Configurações avançadas"
      description="Prioridade, perfil LLM e opções específicas do tipo."
      defaultOpen={false}
    >
      <label className="flex items-center gap-2 text-sm text-content-secondary">
        <input
          type="checkbox"
          checked={inheritProfile}
          onChange={(e) =>
            onChange(subAgent.id, {
              inheritLlmProfile: e.target.checked,
              llmModelLineId: e.target.checked ? '' : subAgent.llmModelLineId,
              llmEffortId: e.target.checked ? '' : subAgent.llmEffortId,
            })
          }
        />
        Herdar perfil LLM do agente
      </label>

      {!inheritProfile && (
        <>
          <Select
            id={`sub_llm_model_line_${subAgent.id}`}
            label="Modelo"
            value={subAgent.llmModelLineId}
            onChange={(e) => onChange(subAgent.id, { llmModelLineId: e.target.value })}
            options={modelLineOptions}
          />
          <Select
            id={`sub_llm_effort_${subAgent.id}`}
            label="Esforço"
            value={subAgent.llmEffortId}
            onChange={(e) => onChange(subAgent.id, { llmEffortId: e.target.value })}
            options={effortOptions}
            disabled={!subAgent.llmModelLineId}
          />
        </>
      )}

      <Input
        id={`sub_priority_${subAgent.id}`}
        label="Prioridade"
        type="number"
        value={subAgent.priority}
        onChange={(e) => onChange(subAgent.id, { priority: e.target.value })}
        placeholder="0"
        hint="Menor número executa primeiro dentro da mesma fase."
      />

      {subAgent.knowledgeBaseIds?.length > 0 && (
        <>
          <FormSegmentedControl
            id={`sub_response_mode_${subAgent.id}`}
            label="Resposta com conhecimento"
            value={subAgent.knowledgeResponseMode || 'creative'}
            options={KNOWLEDGE_RESPONSE_MODE_OPTIONS}
            onChange={(knowledgeResponseMode) =>
              onChange(subAgent.id, { knowledgeResponseMode })
            }
            hint="Criativa (temp. 0.7) sintetiza e complementa; Restrita (temp. 0.2) usa só o conteúdo encontrado."
          />
          <Select
            id={`sub_relevance_${subAgent.id}`}
            label="Modo de relevância (knowledge)"
            value={subAgent.knowledgeRelevanceMode}
            onChange={(e) => onChange(subAgent.id, { knowledgeRelevanceMode: e.target.value })}
            options={RELEVANCE_MODE_OPTIONS}
          />
          <Select
            id={`sub_retrieval_mode_${subAgent.id}`}
            label="Modo de busca no knowledge"
            value={subAgent.knowledgeRetrievalMode || 'focused'}
            onChange={(e) => {
              const knowledgeRetrievalMode = e.target.value;
              onChange(subAgent.id, {
                knowledgeRetrievalMode,
                knowledgeMaxEvidences:
                  knowledgeRetrievalMode === 'diverse'
                    ? KNOWLEDGE_MAX_EVIDENCES_UNLIMITED
                    : subAgent.knowledgeMaxEvidences === KNOWLEDGE_MAX_EVIDENCES_UNLIMITED
                      ? '2'
                      : subAgent.knowledgeMaxEvidences || '2',
              });
            }}
            options={KNOWLEDGE_RETRIEVAL_MODE_OPTIONS}
            hint="Focada traz os trechos mais relevantes. Diversificada traz vários trechos distintos alinhados ao pedido (pode usar várias páginas do mesmo arquivo)."
          />
          {isDiverseRetrieval ? (
            <div className="space-y-1">
              <p className="text-sm font-medium text-content-primary">Máximo de evidências</p>
              <p className="text-sm text-content-secondary">
                Ilimitado no modo diversificado — usa todos os trechos distintos encontrados
                (até o teto interno do sistema).
              </p>
            </div>
          ) : (
            <Select
              id={`sub_max_evidences_${subAgent.id}`}
              label="Máximo de evidências"
              value={
                subAgent.knowledgeMaxEvidences === KNOWLEDGE_MAX_EVIDENCES_UNLIMITED
                  ? '2'
                  : subAgent.knowledgeMaxEvidences || '2'
              }
              onChange={(e) => onChange(subAgent.id, { knowledgeMaxEvidences: e.target.value })}
              options={KNOWLEDGE_MAX_EVIDENCES_OPTIONS}
              hint="Quantidade de trechos e botões de arquivo retornados pelo RAG (melhores por relevância)."
            />
          )}
        </>
      )}

      {subAgent.type === 'guardrails' && (
        <Select
          id={`sub_guardrail_action_${subAgent.id}`}
          label="Ação se falhar"
          value={subAgent.guardrailAction}
          onChange={(e) => onChange(subAgent.id, { guardrailAction: e.target.value })}
          options={GUARDRAIL_ACTION_OPTIONS}
        />
      )}

      {subAgent.type === 'human_loop' && (
        <Textarea
          id={`sub_escalation_${subAgent.id}`}
          label="Mensagem de escalonamento"
          value={subAgent.escalationMessage}
          onChange={(e) => onChange(subAgent.id, { escalationMessage: e.target.value })}
          placeholder="Mensagem enviada ao usuário ao escalar para humano..."
          rows={3}
        />
      )}

      {subAgent.type === 'onboarding' && (
        <div className="space-y-3">
          <label className="flex items-start gap-2 text-sm text-content-secondary">
            <input
              type="checkbox"
              className="mt-1"
              checked={Boolean(subAgent.mcpFieldPersistenceEnabled)}
              onChange={(e) =>
                onChange(subAgent.id, { mcpFieldPersistenceEnabled: e.target.checked })
              }
            />
            <span>
              Guardar no contato os dados retornados pelos MCPs
              <span className="block text-xs text-content-muted mt-0.5">
                Desligado por padrão. Quando ligado, os campos que as ferramentas
                retornarem entram nos dados do contato mesmo que não apareçam na
                conversa — útil quando o roteamento depende deles.
              </span>
            </span>
          </label>

          {subAgent.mcpFieldPersistenceEnabled && (
            <Textarea
              id={`sub_mcp_field_persistence_fields_${subAgent.id}`}
              label="Campos a guardar (opcional)"
              value={subAgent.mcpFieldPersistenceFields || ''}
              onChange={(e) =>
                onChange(subAgent.id, { mcpFieldPersistenceFields: e.target.value })
              }
              placeholder={'classificacao_varejo\nclassificacao_fornecedor'}
              rows={3}
              hint="Um por linha (ou separados por vírgula). Em branco guarda todos os campos retornados."
            />
          )}

          <label className="flex items-center gap-2 text-sm text-content-secondary">
            <input
              type="checkbox"
              checked={subAgent.completionWebhookEnabled || false}
              onChange={(e) =>
                onChange(subAgent.id, { completionWebhookEnabled: e.target.checked })
              }
            />
            Disparar ação ao completar qualificação
          </label>

          {subAgent.completionWebhookEnabled && (
            <>
              <Select
                id={`sub_completion_webhook_delivery_${subAgent.id}`}
                label="Onde executar"
                value={subAgent.completionWebhookDelivery || 'direct'}
                onChange={(e) =>
                  onChange(subAgent.id, { completionWebhookDelivery: e.target.value })
                }
                options={[
                  { value: 'direct', label: 'Core (URL pública)' },
                  { value: 'edge', label: 'Edge Connector (rede privada)' },
                ]}
              />
              {(subAgent.completionWebhookDelivery || 'direct') === 'edge' ? (
                <Input
                  id={`sub_completion_webhook_edge_ref_${subAgent.id}`}
                  label="Referência do endpoint no Edge"
                  value={subAgent.completionWebhookEdgeRef || ''}
                  onChange={(e) =>
                    onChange(subAgent.id, { completionWebhookEdgeRef: e.target.value })
                  }
                  placeholder="webhook:cria-ticket"
                  hint="Deve existir com o mesmo nome em http_endpoints no resources.yaml do Edge."
                  required
                />
              ) : (
                <>
                  <Input
                    id={`sub_completion_webhook_url_${subAgent.id}`}
                    label="URL do webhook"
                    value={subAgent.completionWebhookUrl || ''}
                    onChange={(e) =>
                      onChange(subAgent.id, { completionWebhookUrl: e.target.value })
                    }
                    placeholder="https://meu-crm.com/api/lead"
                    required
                  />
                  <Select
                    id={`sub_completion_webhook_method_${subAgent.id}`}
                    label="Método HTTP"
                    value={subAgent.completionWebhookMethod || 'POST'}
                    onChange={(e) =>
                      onChange(subAgent.id, { completionWebhookMethod: e.target.value })
                    }
                    options={[
                      { value: 'POST', label: 'POST' },
                      { value: 'GET', label: 'GET' },
                    ]}
                  />
                  <Textarea
                    id={`sub_completion_webhook_headers_${subAgent.id}`}
                    label="Headers (JSON, opcional)"
                    value={subAgent.completionWebhookHeaders || ''}
                    onChange={(e) =>
                      onChange(subAgent.id, {
                        completionWebhookHeaders: e.target.value,
                      })
                    }
                    placeholder={'{\n  "Authorization": "Bearer seu-token"\n}'}
                    rows={3}
                    hint='Use um objeto JSON válido. Ex.: {"Authorization":"Bearer seu-token"}.'
                  />
                </>
              )}
            </>
          )}
        </div>
      )}

      <McpServerBindingsEditor
        id={`sub_mcp_bindings_${subAgent.id}`}
        mcpServers={mcpServers}
        bindings={subAgent.mcpBindings || []}
        onChange={(mcpBindings) => onChange(subAgent.id, { mcpBindings })}
        onRequestImprove={
          onRequestImproveMcpInstructions
            ? (binding) => onRequestImproveMcpInstructions(subAgent, binding)
            : undefined
        }
        improvingBindingId={improvingMcpBindingId}
        isImproveDisabled={isMcpImproveDisabled}
        getImproveError={getMcpImproveError}
      />

      {(subAgent.mcpBindings || []).length > 0 && (
        <div className="pt-2">
          <Input
            id={`sub_max_tool_iterations_${subAgent.id}`}
            label="Máximo de iterações de ferramentas (MCP)"
            type="number"
            min="1"
            max="50"
            placeholder="10"
            value={subAgent.maxToolIterations || ''}
            onChange={(e) =>
              onChange(subAgent.id, { maxToolIterations: e.target.value })
            }
            hint="Padrão: 10 iterações. Quantidade máxima de chamadas sucessivas de ferramentas que este sub-agente pode executar por turno (entre 1 e 50)."
          />
        </div>
      )}

      {subAgent.type === 'skills' && (
        <div className="space-y-3 border-t border-surface-border pt-3">
          <label className="flex items-start gap-2 text-sm text-content-secondary">
            <input
              type="checkbox"
              className="mt-1"
              checked={Boolean(subAgent.mcpFollowupReuseEnabled)}
              onChange={(e) =>
                onChange(subAgent.id, { mcpFollowupReuseEnabled: e.target.checked })
              }
            />
            <span>
              Reutilizar evidência MCP em continuações seguras
              <span className="block text-xs text-content-muted mt-0.5">
                Opt-in estrutural. Desligado por padrão (ex.: prontidão permanece
                sem cache). Skills analíticas podem ativar sem nomes hardcoded.
              </span>
            </span>
          </label>

          {subAgent.mcpFollowupReuseEnabled && (
            <Input
              id={`sub_mcp_followup_ttl_${subAgent.id}`}
              label="TTL da evidência (segundos)"
              type="number"
              min="60"
              max="86400"
              value={subAgent.mcpFollowupReuseTtlSeconds || '3600'}
              onChange={(e) =>
                onChange(subAgent.id, { mcpFollowupReuseTtlSeconds: e.target.value })
              }
              hint="Entre 60 e 86400. Após o TTL a evidência não é reutilizada."
            />
          )}

          {(subAgent.executionPhase || 'pre_response') === 'post_response' && (
            <label className="flex items-start gap-2 text-sm text-content-secondary">
              <input
                type="checkbox"
                className="mt-1"
                checked={Boolean(subAgent.postResponseActionOnly)}
                onChange={(e) =>
                  onChange(subAgent.id, { postResponseActionOnly: e.target.checked })
                }
              />
              <span>
                Somente em ações (action-only)
                <span className="block text-xs text-content-muted mt-0.5">
                  Desligado por padrão. Quando ligado, esta skill post_response
                  só roda em intent de ação ou âncora de ação pendente.
                </span>
              </span>
            </label>
          )}

          <div className="space-y-2 border-t border-surface-border pt-3">
            <label className="flex items-start gap-2 text-sm text-content-secondary">
              <input
                type="checkbox"
                className="mt-1"
                checked={Boolean(subAgent.consumeUpstreamSkillsEnabled)}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  onChange(subAgent.id, {
                    consumeUpstreamSkillsEnabled: enabled,
                    consumeUpstreamSkills: enabled ? subAgent.consumeUpstreamSkills || [] : [],
                  });
                }}
              />
              <span>
                Consumir resultado de habilidades anteriores
                <span className="block text-xs text-content-muted mt-0.5">
                  Recebe no contexto os dados e conclusões gerados pelas habilidades que executarem antes desta na sequência.
                </span>
              </span>
            </label>

            {subAgent.consumeUpstreamSkillsEnabled && (
              <div className="ml-6 space-y-2 pt-1">
                {otherSkills.length === 0 ? (
                  <p className="text-xs text-content-muted">
                    Nenhuma outra habilidade configurada no agente. Ao adicionar outras habilidades, você poderá selecioná-las aqui.
                  </p>
                ) : (
                  <>
                    <p className="text-xs font-medium text-content-secondary">
                      Escolha quais habilidades consultar (se nenhuma for marcada, consulta todas as anteriores):
                    </p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto rounded border border-surface-border p-2 bg-surface-subtle">
                      {otherSkills.map((other) => {
                        const targetKey = other.id || other.name;
                        const isSelected =
                          (subAgent.consumeUpstreamSkills || []).includes(targetKey) ||
                          (subAgent.consumeUpstreamSkills || []).includes(other.name);
                        return (
                          <label
                            key={other.id}
                            className="flex items-center gap-2 text-xs text-content-primary cursor-pointer hover:text-content-secondary"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                const current = subAgent.consumeUpstreamSkills || [];
                                const next = e.target.checked
                                  ? [
                                      ...current.filter((k) => k !== targetKey && k !== other.name),
                                      targetKey,
                                    ]
                                  : current.filter((k) => k !== targetKey && k !== other.name);
                                onChange(subAgent.id, { consumeUpstreamSkills: next });
                              }}
                            />
                            <span>
                              {other.name || 'Habilidade sem nome'}
                              {other.priority != null && (
                                <span className="text-content-muted ml-1">
                                  (Prioridade {other.priority})
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </CollapsibleSection>
  );
}

export default SubAgentAdvancedOptions;
